<script setup>
/**
 * Выезжающая панель справки.
 * Показывает контекстную справку для текущей страницы.
 */
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useHelp } from '../composables/useHelp'
import { useIosInstallGuide, isIOS, isStandalone } from '../../composables/useIosInstallGuide'
import { usePwaInstall, openInChrome } from '../../composables/usePwaInstall'
import { t, getLanguage } from '../../services/i18nService'
import { fetchTenantContact } from '../../services/portalService'
import { fetchSupportStatus } from '../../services/supportService'

const router = useRouter()

const {
  isPanelOpen,
  closePanel,
  currentPage,
  currentPageHelp,
  hasPageTour,
  isPageTourCompleted,
  runPageTour,
  runOnboarding,
} = useHelp()

// Состояние раскрытых FAQ
const expandedFaqId = ref(null)

// Контакти техпідтримки дилера
const supportContacts = ref(null)
const supportContactsLoading = ref(false)
const supportContactsLoaded = ref(false)

// Завантажуємо контакти техпідтримки при першому відкритті панелі
const loadSupportContacts = async () => {
  if (supportContactsLoaded.value) return

  supportContactsLoading.value = true
  try {
    const response = await fetchTenantContact()
    supportContacts.value = response?.contact?.supportContacts || null
    supportContactsLoaded.value = true
  } catch {
    supportContacts.value = null
    supportContactsLoaded.value = true
  } finally {
    supportContactsLoading.value = false
  }
}

// Чат підтримки — чи увімкнений для дилера
const supportEnabled = ref(false)
const supportLoaded = ref(false)

const loadSupportChat = async () => {
  if (supportLoaded.value) return
  try {
    const status = await fetchSupportStatus()
    supportEnabled.value = status.supportChatEnabled || false
    supportLoaded.value = true
  } catch {
    supportEnabled.value = false
    supportLoaded.value = true
  }
}

// Перехід до нового чату підтримки
const openNewChat = () => {
  closePanel()
  router.push('/requests/support/new')
}

// Переключение FAQ
const toggleFaq = (faqId) => {
  expandedFaqId.value = expandedFaqId.value === faqId ? null : faqId
}

// Закрытие по Escape
const handleKeydown = (event) => {
  if (event.key === 'Escape' && isPanelOpen.value) {
    closePanel()
  }
}

// Слушаем Escape и загружаем контакты при открытии
watch(isPanelOpen, (open) => {
  if (open) {
    document.addEventListener('keydown', handleKeydown)
    // Завантажуємо контакти техпідтримки та стан чату при першому відкритті
    loadSupportContacts()
    loadSupportChat()
  } else {
    document.removeEventListener('keydown', handleKeydown)
  }
})

// PERFORMANCE: очистка event listener при размонтировании, если панель была открыта
onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeydown)
})

// Заголовок страницы справки
const pageTitle = computed(() => {
  if (currentPageHelp.value?.titleKey) {
    return t(currentPageHelp.value.titleKey)
  }
  return t('help.panel.title')
})

// FAQ текущей страницы
const pageFaq = computed(() => {
  return currentPageHelp.value?.faq || []
})

// Розділи онлайн-інструкції. Ключі сторінок з useHelp збігаються зі слагами
// інструкції всюди, крім карти: там сторінка devices, а розділ transport.
const MANUAL_SECTIONS = {
  dashboard: 'dashboard',
  devices: 'transport',
  reports: 'reports',
  geozones: 'geozones',
  maintenance: 'maintenance',
  drivers: 'drivers',
  personnel: 'personnel',
  trailers: 'trailers',
  regtech: 'regtech',
  notifications: 'notifications',
  subusers: 'subusers',
  sharing: 'sharing',
  billing: 'billing',
  requests: 'requests',
  // Ключ = route.name, а он у стрічки саме 'whats-new' (див. useHelp.currentPage)
  'whats-new': 'whats-new',
}
const MANUAL_LANGS = ['uk', 'ru', 'en', 'pl']

// Посилання відносне: інструкція лежить на тому ж домені, що й портал, тому
// в партнера відкриється його ж копія з його брендингом.
const manualUrl = computed(() => {
  const lang = MANUAL_LANGS.includes(getLanguage()) ? getLanguage() : 'uk'
  const section = MANUAL_SECTIONS[currentPage.value]
  return section ? `/manual/${lang}/${section}.html` : `/manual/${lang}/`
})

// Запуск тура страницы
const handleStartTour = () => {
  runPageTour(true)
}

// Запуск onboarding
const handleStartOnboarding = () => {
  runOnboarding(true)
}

// Гайд встановлення PWA на iPhone — показуємо пункт лише на iOS (і лише якщо ще не встановлено)
const { openGuide: openIosInstallGuide } = useIosInstallGuide()
const showIosInstallOption = computed(() => isIOS() && !isStandalone())
const handleOpenIosInstallGuide = () => {
  closePanel()
  openIosInstallGuide(true)
}

// Android/Desktop Chrome: пряма установка через beforeinstallprompt
// Samsung Internet: нативну установку не пропонуємо — його WebAPK ріже Play Protect,
// замість неї ведемо юзера відкрити сайт у Chrome
const { canInstallNative, canOpenInChrome, triggerNativeInstall } = usePwaInstall()
const handleNativeInstall = async () => {
  const { outcome } = await triggerNativeInstall()
  if (outcome === 'accepted') closePanel()
}
const handleOpenInChrome = () => {
  closePanel()
  openInChrome()
}
</script>

<template>
  <Teleport to="body">
    <!-- Backdrop -->
    <Transition name="fade">
      <div
        v-if="isPanelOpen"
        class="help-panel-backdrop"
        @click="closePanel"
      ></div>
    </Transition>

    <!-- Panel -->
    <Transition name="slide">
      <aside
        v-if="isPanelOpen"
        class="help-panel"
        role="dialog"
        aria-modal="true"
        :aria-label="t('help.panel.title')"
      >
        <!-- Header -->
        <header class="help-panel__header">
          <h2 class="help-panel__title">{{ t('help.panel.title') }}</h2>
          <button
            class="help-panel__close"
            type="button"
            :aria-label="t('help.closeHelp')"
            @click="closePanel"
          >
            <span class="material-symbols-outlined">close</span>
          </button>
        </header>

        <!-- Scrollable Content / Прокручуваний контент -->
        <div class="help-panel__content">
          <!-- Онлайн-інструкція: перший блок панелі, відкривається на розділі
               поточної сторінки. Стоїть вище за FAQ і тури навмисно: це найповніша
               відповідь, а не остання надія після довгого списку питань -->
          <section class="help-section">
            <a
              class="help-action-btn help-action-btn--manual"
              :href="manualUrl"
              target="_blank"
              rel="noopener"
            >
              <span class="material-symbols-outlined">menu_book</span>
              <span>{{ t('help.panel.manual') }}</span>
              <span class="material-symbols-outlined help-action-btn__external" aria-hidden="true">open_in_new</span>
            </a>
            <p class="help-section__manual-hint">{{ t('help.panel.manualHint') }}</p>
          </section>

          <!-- Встановлення додатка — окрема секція, видима і на мобільних -->
          <section v-if="showIosInstallOption || canInstallNative || canOpenInChrome" class="help-section help-section--ios-install">
            <!-- iOS: показуємо анімований гайд -->
            <button
              v-if="showIosInstallOption"
              class="help-action-btn help-action-btn--ios-install"
              type="button"
              @click="handleOpenIosInstallGuide"
            >
              <span class="material-symbols-outlined">install_mobile</span>
              <span>{{ t('pwa.iosGuide.launchFromHelp') }}</span>
            </button>
            <!-- Android/Chrome: прямий запуск установки -->
            <button
              v-else-if="canInstallNative"
              class="help-action-btn help-action-btn--ios-install"
              type="button"
              @click="handleNativeInstall"
            >
              <span class="material-symbols-outlined">install_mobile</span>
              <span>{{ t('pwa.install.installOnAndroid') }}</span>
            </button>
            <!-- Samsung Internet: установка звідси ламається Play Protect — відкриваємо в Chrome -->
            <button
              v-else-if="canOpenInChrome"
              class="help-action-btn help-action-btn--ios-install"
              type="button"
              @click="handleOpenInChrome"
            >
              <span class="material-symbols-outlined">open_in_new</span>
              <span>{{ t('pwa.install.openInChrome') }}</span>
            </button>
          </section>

          <!-- Current page info -->
          <section v-if="currentPageHelp" class="help-section">
            <div class="help-section__page-indicator">
              <span class="material-symbols-outlined">location_on</span>
              <span>{{ pageTitle }}</span>
            </div>

            <!-- Page overview -->
            <p v-if="currentPageHelp.overviewKey" class="help-section__overview">
              {{ t(currentPageHelp.overviewKey) }}
            </p>

            <!-- Start tour button -->
            <button
              v-if="hasPageTour"
              class="help-action-btn help-action-btn--primary"
              type="button"
              @click="handleStartTour"
            >
              <span class="material-symbols-outlined">play_circle</span>
              <span>{{ t('help.panel.startTour') }}</span>
              <span v-if="isPageTourCompleted" class="help-action-btn__badge">
                <span class="material-symbols-outlined">check_circle</span>
              </span>
            </button>
          </section>

          <!-- FAQ Section -->
          <section v-if="pageFaq.length > 0" class="help-section">
            <h3 class="help-section__title">
              <span class="material-symbols-outlined">help_outline</span>
              {{ t('help.panel.faq') }}
            </h3>

            <ul class="help-faq-list">
              <li
                v-for="faq in pageFaq"
                :key="faq.id"
                class="help-faq-item"
                :class="{ 'help-faq-item--expanded': expandedFaqId === faq.id }"
              >
                <button
                  class="help-faq-item__question"
                  type="button"
                  :aria-expanded="expandedFaqId === faq.id"
                  @click="toggleFaq(faq.id)"
                >
                  <span>{{ t(faq.questionKey) }}</span>
                  <span class="material-symbols-outlined help-faq-item__chevron">
                    expand_more
                  </span>
                </button>
                <div v-if="expandedFaqId === faq.id" class="help-faq-item__answer">
                  {{ t(faq.answerKey) }}
                </div>
              </li>
            </ul>
          </section>

          <!-- Other tours section - приховано на мобільних -->
          <section class="help-section help-section--tours">
            <h3 class="help-section__title">
              <span class="material-symbols-outlined">school</span>
              {{ t('help.panel.otherTours') }}
            </h3>

            <button
              class="help-action-btn help-action-btn--tour"
              type="button"
              @click="handleStartOnboarding"
            >
              <span class="material-symbols-outlined">waving_hand</span>
              <span>{{ t('help.panel.onboardingTour') }}</span>
            </button>

          </section>

        </div>

        <!-- Fixed Support Footer / Закріплений блок контактів -->
        <footer class="help-panel__footer">
          <section class="help-section help-section--support">
            <h3 class="help-section__title">
              <span class="material-symbols-outlined">support_agent</span>
              {{ t('help.panel.needHelp') }}
            </h3>
            <!-- Показуємо контакти дилера якщо вони задані, інакше показуємо стандартний текст -->
            <div v-if="supportContactsLoading" class="help-section__support-loading">
              {{ t('common.loading') || 'Завантаження...' }}
            </div>
            <div
              v-else-if="supportContacts"
              class="help-section__support-contacts"
              v-html="supportContacts"
            ></div>
            <p v-else class="help-section__support-text">
              {{ t('help.panel.contactSupport') }}
            </p>

            <!-- Кнопка нового чату підтримки -->
            <button
              v-if="supportEnabled"
              class="help-chat-btn"
              type="button"
              @click="openNewChat"
            >
              <span class="material-symbols-outlined">chat</span>
              <span>{{ t('help.panel.startChat') }}</span>
            </button>
          </section>
        </footer>
      </aside>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* Backdrop - z-index вище за top-bar (10000) */
.help-panel-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10100;
  background: rgba(0, 0, 0, 0.4);
}

/* Panel - z-index вище за backdrop */
.help-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 10101;
  width: 360px;
  max-width: 100%;
  background: var(--bg-card);
  box-shadow: var(--shadow-md);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header */
.help-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: calc(16px + env(safe-area-inset-top, 0px)) 20px 16px 20px; /* Відступ для "чілки" iPhone */
  border-bottom: 1px solid var(--border, var(--border-default));
  background: var(--bg-card);
}

.help-panel__title {
  margin: 0;
  font-size: var(--text-lg);
  font-weight: 600;
  color: var(--text-primary));
}

.help-panel__close {
  padding: 8px;
  border: none;
  background: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  color: var(--muted, var(--text-muted));
  transition: background-color 0.15s ease, color 0.15s ease;
}

.help-panel__close:hover {
  background: var(--hover, var(--bg-tertiary));
  color: var(--text-primary));
}

.help-panel__close .material-symbols-outlined {
  font-size: 24px;
}

/* Content - прокручуваний контент */
.help-panel__content {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  min-height: 0; /* Важливо для правильного скролу у flex-контейнері */
}

/* Footer - закріплений блок контактів */
.help-panel__footer {
  flex-shrink: 0;
  padding: 0 20px 16px 20px;
  background: var(--bg-card);
  border-top: 1px solid var(--border, var(--border-default));
}

/* Sections */
.help-section {
  margin-bottom: 24px;
}

.help-section:last-child {
  margin-bottom: 0;
}

.help-section__title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 12px 0;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--muted, var(--text-muted));
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.help-section__title .material-symbols-outlined {
  font-size: 18px;
}

.help-section__page-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: var(--primary-light, var(--bg-tertiary));
  border-radius: var(--radius-md);
  color: var(--primary, var(--primary-hover));
  font-weight: 500;
  margin-bottom: 12px;
}

.help-section__page-indicator .material-symbols-outlined {
  font-size: 20px;
}

.help-section__overview {
  margin: 0 0 16px 0;
  font-size: var(--text-sm);
  line-height: 1.6;
  color: var(--text-secondary, var(--text-secondary));
}

/* Action buttons */
.help-action-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 14px;
  border: 1px solid var(--border, var(--border-default));
  background: var(--bg-card);
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--text-primary));
  text-align: left;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  margin-bottom: 8px;
}

.help-action-btn:last-child {
  margin-bottom: 0;
}

.help-action-btn:hover {
  background: var(--hover, var(--bg-secondary));
  border-color: var(--border-hover, var(--border-strong));
}

.help-action-btn .material-symbols-outlined {
  font-size: 20px;
  color: var(--muted, var(--text-muted));
}

.help-action-btn--primary {
  background: var(--primary, var(--primary-hover));
  border-color: var(--primary, var(--primary-hover));
  color: white;
}

.help-action-btn--primary:hover {
  background: var(--primary-hover, var(--primary-dark));
  border-color: var(--primary-hover, var(--primary-dark));
}

.help-action-btn--primary .material-symbols-outlined {
  color: white;
}

/* Посилання на онлайн-інструкцію: та сама кнопка, але це <a>, тому знімаємо
   підкреслення і успадковуємо колір тексту */
.help-action-btn--manual {
  text-decoration: none;
  color: var(--text-primary);
}

.help-action-btn__external {
  margin-left: auto;
}

.help-section__manual-hint {
  margin: 6px 0 0;
  font-size: var(--text-xs);
  color: var(--text-muted);
}

.help-action-btn__badge {
  margin-left: auto;
}

.help-action-btn__badge .material-symbols-outlined {
  font-size: 18px;
  color: var(--success, var(--green-500));
}

.help-action-btn--primary .help-action-btn__badge .material-symbols-outlined {
  color: rgba(255, 255, 255, 0.8);
}

/* FAQ */
.help-faq-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.help-faq-item {
  border: 1px solid var(--border, var(--border-default));
  border-radius: var(--radius-md);
  margin-bottom: 8px;
  overflow: hidden;
}

.help-faq-item:last-child {
  margin-bottom: 0;
}

.help-faq-item__question {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--text-primary));
  text-align: left;
  transition: background-color 0.15s ease;
}

.help-faq-item__question:hover {
  background: var(--hover, var(--bg-secondary));
}

.help-faq-item__chevron {
  font-size: var(--text-xl);
  color: var(--muted, var(--text-muted));
  transition: transform 0.2s ease;
}

.help-faq-item--expanded .help-faq-item__chevron {
  transform: rotate(180deg);
}

.help-faq-item__answer {
  padding: 0 14px 14px 14px;
  font-size: var(--text-sm);
  line-height: 1.6;
  color: var(--text-secondary, var(--text-secondary));
  border-top: 1px solid var(--border, var(--border-default));
  padding-top: 12px;
  margin-top: -1px;
  background: var(--bg-secondary));
}

/* Support section / Секція контактів техпідтримки */
.help-section--support {
  padding: 16px;
  background: var(--bg-secondary));
  border-radius: var(--radius-md);
  margin-top: 16px;
  margin-bottom: 0;
}

.help-section__support-text {
  margin: 0;
  font-size: var(--text-sm);
  color: var(--text-secondary, var(--text-secondary));
}

.help-section__support-loading {
  font-size: var(--text-sm);
  color: var(--muted, var(--text-muted));
  font-style: italic;
}

/* Стилі для HTML-контенту контактів техпідтримки */
.help-section__support-contacts {
  font-size: var(--text-sm);
  line-height: 1.6;
  color: var(--text-secondary, var(--text-secondary));
}

.help-section__support-contacts :deep(a) {
  color: var(--primary, var(--primary-hover));
  text-decoration: none;
}

.help-section__support-contacts :deep(a:hover) {
  text-decoration: underline;
}

.help-section__support-contacts :deep(p) {
  margin: 0 0 8px 0;
}

.help-section__support-contacts :deep(p:last-child) {
  margin-bottom: 0;
}

/* Кнопка чату підтримки */
.help-chat-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 14px;
  margin-top: 12px;
  background: var(--primary, var(--primary-hover));
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: var(--text-sm);
  font-weight: 500;
  color: white;
  text-align: left;
  transition: background-color 0.15s ease;
}

.help-chat-btn:hover {
  background: var(--primary-hover, var(--primary-dark));
}

.help-chat-btn .material-symbols-outlined {
  font-size: 20px;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.25s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}

/* Mobile */
@media (max-width: 480px) {
  .help-panel {
    width: 100%;
  }
}

/* Mobile adaptations - приховуємо туры на мобільних пристроях */
@media (max-width: 768px) {
  /* Приховуємо секцію турів і кнопки турів на мобільних */
  .help-section--tours,
  .help-action-btn--primary,
  .help-action-btn--tour {
    display: none !important;
  }
}

/* Гайд встановлення на iPhone — видимий і на мобільних (там він і потрібен) */
.help-section--ios-install {
  padding-bottom: 4px;
}
.help-action-btn--ios-install {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-hover) 100%);
  color: #fff;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(52, 120, 246, 0.25);
  transition: transform 0.15s, box-shadow 0.15s;
}
.help-action-btn--ios-install:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(52, 120, 246, 0.35);
}
.help-action-btn--ios-install .material-symbols-outlined {
  font-size: 22px;
}
</style>
